<template>
  <b-modal
    id="parameter-edit-modal"
    ref="modal"
    :title="`${$t('Editar Parâmetro')}: ${parameter.name || parameter.key || ''}`"
    size="lg"
    hide-footer
    :no-close-on-backdrop="loading"
    :no-close-on-esc="loading"
    @shown="onShown"
  >
    <FormulateForm
      ref="form"
      @submit="save"
    >
      <b-alert
        v-if="validationError"
        variant="danger"
        show
      >
        {{ validationError }}
      </b-alert>

      <b-row>
        <b-col md="6">
          <FormulateInput
            id="param-key"
            :value="parameter.key"
            type="text"
            :label="$t('Chave')"
            disabled
          />
        </b-col>
        <b-col md="6">
          <FormulateInput
            id="param-name"
            :value="parameter.name"
            type="text"
            :label="$t('Nome')"
            @input="parameter.name = $event"
          />
        </b-col>
      </b-row>

      <b-row>
        <b-col md="12">
          <FormulateInput
            id="param-description"
            :value="parameter.description"
            type="textarea"
            :label="$t('Descrição')"
            @input="parameter.description = $event"
          />
        </b-col>
      </b-row>

      <b-row>
        <b-col md="12">
          <!-- Boolean -->
          <div v-if="isBoolean()">
            <label>{{ $t('Valor') }}</label>
            <b-form-checkbox
              v-model="parameter.value"
              switch
              :checked="parameter.value === 'true' || parameter.value === 'True'"
              @change="parameter.value = $event ? 'true' : 'false'"
            >
              {{ parameter.value === 'true' || parameter.value === 'True' ? $t('Verdadeiro') : $t('Falso') }}
            </b-form-checkbox>
            <small class="text-muted d-block mt-2">{{ getValueHint() }}</small>
          </div>

          <!-- Numeric (Int, Decimal, Double) -->
          <FormulateInput
            v-else-if="isNumeric()"
            id="param-value"
            :value="parameter.value"
            type="number"
            :label="$t('Valor')"
            validation="required"
            :hint="getValueHint()"
            step="any"
            :instruction="parameter.clrType ? `${$t('O valor deve ser compatível com o tipo')}: ${getTypeName(parameter.clrType)}` : ''"
            @input="parameter.value = $event"
          />

          <!-- DateTime -->
          <FormulateInput
            v-else-if="isDateTime()"
            id="param-value"
            :value="parameter.value"
            type="date"
            :label="$t('Valor')"
            validation="required"
            :hint="getValueHint()"
            :instruction="parameter.clrType ? `${$t('O valor deve ser compatível com o tipo')}: ${getTypeName(parameter.clrType)}` : ''"
            @input="parameter.value = $event"
          />

          <!-- List/Array (JSON) ou String padrão -->
          <FormulateInput
            v-else
            id="param-value"
            :value="parameter.value"
            type="textarea"
            :label="$t('Valor')"
            validation="required"
            :hint="getValueHint()"
            :instruction="parameter.clrType ? `${$t('O valor deve ser compatível com o tipo')}: ${getTypeName(parameter.clrType)}` : ''"
            @input="parameter.value = $event"
          />
        </b-col>
      </b-row>

      <b-row class="mt-3">
        <b-col class="d-flex justify-content-end gap-2">
          <b-button
            variant="outline-primary"
            @click="close"
            :disabled="loading"
          >
            {{ $t('Cancelar') }}
          </b-button>
          <b-button
            variant="primary"
            @click="save"
            :disabled="loading"
          >
            <b-spinner small v-if="loading" class="mr-2" />
            {{ loading ? $t('Salvando...') : $t('Salvar') }}
          </b-button>
        </b-col>
      </b-row>
    </FormulateForm>
  </b-modal>
</template>

<script>
import { BRow, BCol, BModal, BButton, BAlert, BSpinner, BFormCheckbox } from 'bootstrap-vue'

export default {
  name: 'ParameterEditModal',
  components: {
    BRow,
    BCol,
    BModal,
    BButton,
    BAlert,
    BSpinner,
    BFormCheckbox,
  },
  data() {
    return {
      parameter: {
        key: '',
        name: '',
        value: '',
        description: '',
        clrType: '',
        group: '',
        allowedProfiles: [],
      },
      originalParameter: null,
      validationError: null,
      loading: false,
    }
  },
  methods: {
    open(parameter) {
      this.originalParameter = parameter
      this.parameter = { ...parameter }
      this.validationError = null
      this.$refs.modal.show()
    },
    save() {
      this.validationError = null

      // Validação básica do Formulate
      if (!this.$refs.form.isValid) {
        return
      }

      // Validação do tipo de valor
      if (this.parameter.clrType && !this.validateValueType(this.parameter)) {
        this.validationError = this.$t(`Valor '{0}' não é compatível com o tipo '{1}'`).replace('{0}', this.parameter.value).replace('{1}', this.getTypeName(this.parameter.clrType))
        return
      }

      // Chamada à API
      this.saveToApi()
    },
    async saveToApi() {
      try {
        this.loading = true

        await this.$http.put('/api/parameter', {
          key: this.parameter.key || this.parameter.id,
          name: this.parameter.name,
          value: this.parameter.value,
          description: this.parameter.description,
        })

        this.showSuccess({ message: this.$t('Parâmetro atualizado com sucesso') })
        this.$emit('saved')
        this.$nextTick(() => {
          this.close()
        })
      } catch (error) {
        this.showError({ error, message: this.$t('Erro ao salvar parâmetro') })
      } finally {
        this.loading = false
      }
    },
    close() {
      if (!this.loading) {
        this.$refs.modal.hide()
      }
    },
    onShown() {
      // Focus on value input when modal opens
      this.$nextTick(() => {
        const input = this.$refs.form.$el.querySelector('#param-value textarea')
        if (input) input.focus()
      })
    },
    getTypeName(clrType) {
      if (!clrType) return ''
      const parts = clrType.split('.')
      let typeName = parts[parts.length - 1]
      typeName = typeName.replace(/`\d+/, '')
      return typeName
    },
    validateValueType(parameter) {
      const { clrType, value } = parameter

      if (!clrType || !value) return true

      try {
        if (clrType.includes('Boolean')) {
          return value === 'true' || value === 'false' || value === 'True' || value === 'False'
        }

        if (clrType.includes('Int32') || clrType.includes('Int64')) {
          return !Number.isNaN(parseInt(value, 10))
        }

        if (clrType.includes('Decimal') || clrType.includes('Double')) {
          return !Number.isNaN(parseFloat(value))
        }

        if (clrType.includes('DateTime')) {
          return !Number.isNaN(Date.parse(value))
        }

        if (clrType.includes('List') || clrType.includes('Array')) {
          JSON.parse(value)
          return true
        }

        return true
      } catch {
        return false
      }
    },
    getValueHint() {
      if (!this.parameter.clrType) {
        return this.$t('Digite o valor do parâmetro')
      }

      const typeName = this.getTypeName(this.parameter.clrType)

      if (typeName.includes('Boolean')) {
        return this.$t('Ative ou desative a opção')
      }

      if (typeName.includes('Int')) {
        return this.$t('Deve ser um número inteiro')
      }

      if (typeName.includes('Decimal') || typeName.includes('Double')) {
        return this.$t('Deve ser um número com decimais')
      }

      if (typeName.includes('DateTime')) {
        return this.$t('Deve estar em formato de data válido')
      }

      if (typeName.includes('List') || typeName.includes('Array')) {
        return this.$t(`Deve ser um JSON válido em formato de array. Ex: [1,2,3] ou ["a","b"]`)
      }
      return this.$t('Digite o valor do parâmetro')
    },
    isBoolean() {
      return this.parameter.clrType && this.parameter.clrType.includes('Boolean')
    },
    isNumeric() {
      if (!this.parameter.clrType) return false
      return this.parameter.clrType.includes('Integer') ||
             this.parameter.clrType.includes('Int32') || 
             this.parameter.clrType.includes('Int64') || 
             this.parameter.clrType.includes('Decimal') || 
             this.parameter.clrType.includes('Double')
    },
    isDateTime() {
      return this.parameter.clrType && this.parameter.clrType.includes('DateTime')
    },
  },
}
</script>

<style scoped>
.alert {
  margin-bottom: 1rem;
}
</style>
